import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardWidgets } from '../../lib/dashboardWidgets';

function copyText(text) {
  const fallback = () => {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (_) { /* nothing more we can do */ }
    document.body.removeChild(ta);
  };
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p && typeof p.catch === 'function') p.catch(fallback); else if (!p) fallback();
  } catch (_) { fallback(); }
}

/** Small "ID 7Q2K4 · copy" pill — share with the teacher so they can look the student up. */
export function StudentIdPill({ dark = false }) {
  const { user } = useAuth();
  const widgets = useDashboardWidgets();
  const [copied, setCopied] = useState(false);
  if (!widgets.student_id_pill || !user?.student_id) return null;
  const copy = () => { copyText(user.student_id); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button type="button" onClick={copy} title="Your Student ID — share it with your instructor so they can look you up. Click to copy."
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-mono transition-colors ${dark ? 'border-white/15 bg-white/5 text-slate-300 hover:bg-white/10' : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
      data-testid="dashboard-student-id" data-copied={copied}>
      <span className="opacity-70">ID</span><span className={`font-bold tracking-wider ${dark ? 'text-white' : 'text-slate-800'}`}>{user.student_id}</span>
      {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} className="opacity-60" />}
    </button>
  );
}

/** "Step 2 of 5" dots shared by both hero cards; links to the full checklist on Start Here. */
export default function StepDots({ steps, activeKey, dark = false }) {
  const idx = Math.max(0, steps.findIndex((s) => s.key === activeKey));
  const base = dark ? 'text-slate-400' : 'text-slate-500';
  return (
    <div className="flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-testid="ns-step-dots">
      <div className="flex items-center gap-1.5">
        {steps.map((s, i) => {
          const done = s.status === 'complete';
          const active = s.key === activeKey;
          const cls = done ? 'bg-emerald-500 text-white' : active ? (dark ? 'bg-amber-400 text-slate-900 ring-4 ring-amber-400/25' : 'bg-[#002FA7] text-white ring-4 ring-blue-200') : (dark ? 'bg-white/10 text-slate-400' : 'bg-slate-200 text-slate-500');
          return (
            <span key={s.key} title={s.title} className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full text-[10px] sm:text-[11px] font-bold flex items-center justify-center transition-colors ${cls}`} data-testid={`ns-dot-${s.key}`} data-status={s.status}>
              {done ? <Check size={11} strokeWidth={3} /> : i + 1}
            </span>
          );
        })}
      </div>
      <span className={`text-xs font-semibold ${base}`} data-testid="ns-step-position">Step {idx + 1} of {steps.length}</span>
      <Link to="/start-here" className={`text-xs font-semibold underline-offset-2 hover:underline ${dark ? 'text-slate-300' : 'text-[#002FA7]'}`} data-testid="ns-all-steps-link">
        <span className="sm:hidden">All steps →</span><span className="hidden sm:inline">See all steps →</span>
      </Link>
      <span className="ml-auto"><StudentIdPill dark={dark} /></span>
    </div>
  );
}
